'use strict';
/* ==========================================================================
   levels/pamphlet.js — step "pamphlet" (any, BONUS: "stamp": false, so it never counts toward AMENDED): THE PAMPHLET,
   a read-along "research paper" (content/paper.json, PAPER.md; why a DOM paper: core-local/PAPER_READER.md).

   The page is a DOM overlay (core-local/paper_reader.js) over the stage; this scene is the narrator and the stage:
     • the narrator clock: one voiced sentence at a time (paper.json ids). A clip `vo:<id>` plays when it is loaded
       (Audio.schedule on the 'pamphlet' group, the Web Audio clock); otherwise the sentence is TIMED SILENTLY from its
       spoken word count (150 words a minute, window.__timeScale speeds it: tests). Word timings (assets/vo/
       paper_words.json, else Audio.words) light the current word; the shown text is aligned to the spoken words
       (numbers are spoken as words) by an LCS on the normalized tokens.
     • follow mode: the page scrolls gently to keep the current sentence at ~28 % of the column. A wheel / drag /
       arrow key pauses auto-scroll (the narration carries on) and shows "Back to the reader" (B). Tapping a sentence or
       a section heading reads from there (and follows again).
     • each section ends in its quick check: reading waits there until it is answered right or skipped; the first try
       is scored (summary.score). Options are shuffled once per student (a saved seed), keys 1-3 pick by position.
     • "Jump to You Just Learned" (S, ⏭; visible from the start) jumps to the summary and reads it.
     • layouts: 'wide' (a landscape stage ≥ ~1150 CSS px: the figure of the section being read beside the text, drawn on
       the stage with core-local/paper_figs.js, its part lit by the sentence), 'narrow' (a phone held sideways: the cover
       + controls in a strip on the left, figures inline in the page), 'dock' (a phone held upright: the stage shows the
       cover, the page fills the dock's sheet, controls in the dock's bar). Term cards open over the stage / figure
       area, never over the text or the controls.
   Progress: Game.progress.pamphlet = {at, seed, checks: {secId: {order, first, right, wrong, skipped}}, done, finished}
   (lesson.json "progress"); `at` = the sentence to resume from.
   Voice (when the assets job has made them, listed in lesson.json assets.pamphlet): vo/<id>.wav (glob vo/p*.wav),
   vo/paper_lines.json, vo/paper_words.json, vo/paper_who.json. Until then: silent timing, nothing requested.

   QA hooks: sc.state() {idx, id, phase, playing, gate, follow, mode, pos, dur, clip, word, done, score},
   sc.seq (the reading order), sc.jumpTo(id, play), sc.play(), sc.pause(), sc.toggle(), sc.summary(), sc.back(),
   sc.answer(secId, i), sc.skipCheck(secId), sc.keepReading(), sc.term(term), sc.reader (PaperReader), sc.layoutNow.
   ========================================================================== */
(function () {
  const STEP = 'pamphlet', GROUP = 'pamphlet';
  const CC = () => (window.Civic && Civic.C) || { navy: '#1f2f55', brick: '#9e2b25', cream: '#f3ead7', gold: '#b08a2e', parchment: '#efe2c2', ink: '#2b2420' };
  const TS = () => (typeof window.__timeScale === 'number' && window.__timeScale > 0 ? window.__timeScale : 1);
  const WPM = 150;
  const VOICE = { lines: 'vo/paper_lines.json', words: 'vo/paper_words.json', who: 'vo/paper_who.json', clips: 'vo/p*.wav' };

  /* ---------- small pure helpers ---------- */
  const spokenWords = s => String(s.tts || s.text || '').split(/\s+/).filter(Boolean).length;
  const silentDur = s => Math.max(1.0, spokenWords(s) * 60 / WPM + 0.15);
  const gapAfter = s => (s.kind === 'title' ? 0.8 : s.kind === 'head' ? 0.6 : s.paraEnd ? 0.6 : 0.3);
  function rng(seed) { let x = (seed >>> 0) || 1; return () => { x = (Math.imul(x, 1664525) + 1013904223) >>> 0; return x / 4294967296; }; }
  function shuffled(n, seed) { const a = [...Array(n).keys()], r = rng(seed); for (let i = n - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }
  const normTok = w => String(w).toLowerCase().replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '');
  /* display words ↔ spoken words: an LCS on normalized tokens; unmatched display words share the unmatched spoken
     span between their anchors (e.g. "1787" ↔ "seventeen eighty-seven") → [{s, e}] per display word */
  function alignWords(display, spoken) {
    const D = display.map(normTok), S = spoken.map(w => normTok(w.w));
    const n = D.length, m = S.length, L = Array.from({ length: n + 1 }, () => new Int16Array(m + 1));
    for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) L[i][j] = D[i] && D[i] === S[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
    const pair = new Array(n).fill(-1);
    for (let i = 0, j = 0; i < n && j < m;) { if (D[i] && D[i] === S[j]) { pair[i] = j; i++; j++; } else if (L[i + 1][j] >= L[i][j + 1]) i++; else j++; }
    const out = new Array(n);
    let i = 0;
    while (i < n) {
      if (pair[i] >= 0) { const w = spoken[pair[i]]; out[i] = { s: w.s, e: w.e }; i++; continue; }
      let k = i; while (k < n && pair[k] < 0) k++;
      const prevJ = i > 0 ? pair[i - 1] : -1, nextJ = k < n ? pair[k] : m;
      const s0 = prevJ + 1 < m ? (spoken[prevJ + 1] || {}).s : null, e0 = nextJ - 1 >= 0 ? (spoken[nextJ - 1] || {}).e : null;
      const a = s0 != null && nextJ > prevJ + 1 ? s0 : (prevJ >= 0 ? spoken[prevJ].e : 0);
      const b = e0 != null && nextJ > prevJ + 1 ? e0 : (nextJ < m ? spoken[nextJ].s : (m ? spoken[m - 1].e : 0));
      const cnt = k - i;
      for (let q = 0; q < cnt; q++) out[i + q] = { s: a + (b - a) * q / cnt, e: a + (b - a) * (q + 1) / cnt };
      i = k;
    }
    return out;
  }

  class Pamphlet extends Scene {
    enter() {
      const json = Game.content && Game.content.paper;
      this.err = json && Array.isArray(json.sections) ? null : 'content/paper.json is missing';
      if (this.err) return;
      this.M = PaperReader.model(json);
      this.seq = this.M.seq;
      this.byId = {}; this.seq.forEach(s => { this.byId[s.id] = s; });
      this.secOf = {}; this.M.sections.forEach(S => { this.secOf[S.id] = S; });
      if (!Game.progress.pamphlet || typeof Game.progress.pamphlet !== 'object') Game.progress.pamphlet = {};
      const P = this.P = Game.progress.pamphlet;
      if (!P.seed) P.seed = 100000 + Math.floor(Math.random() * 900000);
      if (!P.checks) P.checks = {};
      this.M.sections.forEach((S, i) => {
        if (!S.check) return;
        const c = P.checks[S.id] || (P.checks[S.id] = {});
        const n = (S.check.options || []).length;
        if (!Array.isArray(c.order) || c.order.length !== n) c.order = shuffled(n, P.seed + i * 7919);
        if (!Array.isArray(c.wrong)) c.wrong = [];
      });
      const at = this.byId[P.at] ? this.byId[P.at].i : 0;
      this.idx = P.finished ? 0 : at;
      this.pos = 0; this.cur = null; this.playing = false; this.gate = null; this.follow = true; this.done = !!P.finished;
      this.word = -1; this.mode = null; this.hiCache = {}; this.voice = { words: {}, who: {}, lines: {} };
      this.startedOnce = false;
      this.reader = PaperReader.create(this.M, {
        onSentence: id => this.jumpTo(id, true),
        onTerm: (t, sec) => this.term(t, sec),
        onCheck: (sec, i) => this.answer(sec, i),
        onSkip: sec => this.skipCheck(sec),
        onContinue: () => this.keepReading(),
        onBack: () => this.back(),
        onUserScroll: () => this.userScrolled(),
        onEnd: a => (a === 'map' ? this.toMap() : this.again()),
        drawFigure: (c, fig, o) => { if (window.PaperFigs) PaperFigs.draw(c, fig, o); },
        speaker: who => {
          const sp = (window.LessonHooks && LessonHooks.speakers && LessonHooks.speakers[who]) || {};
          return { name: sp.name || (who ? who[0].toUpperCase() + who.slice(1) : ''), color: sp.color || CC().brick };
        },
      });
      Game.progress.save();
      this.refreshChecks();
      this.loadVoice();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.12 });
      this._revealOnShow = true;
    }
    exit() {
      Audio.stopGroup(GROUP); Audio.duck(false);
      if (this.reader) { this.reader.destroy(); this.reader = null; }
      if (this.P) Game.progress.save();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
    }

    /* ---------- voice data (only what lesson.json's pamphlet tier lists: nothing is requested before it exists) ---------- */
    tierHas(ref) { const t = (Game.lesson && Game.lesson.assets && Game.lesson.assets[STEP]) || []; return t.includes(ref); }
    loadVoice() {
      const get = (k, ref) => {
        if (!this.tierHas(ref) || !window.Loader) return;
        Loader.json(Assets.url(ref)).then(d => { if (d && typeof d === 'object' && this.voice) this.voice[k] = d; }, () => {});
      };
      get('lines', VOICE.lines); get('words', VOICE.words); get('who', VOICE.who);
    }
    wordsOf(id) {
      const w = (this.voice.words && this.voice.words[id]) || (Audio.words && Audio.words[id]);
      return w && Array.isArray(w.words) && w.words.length ? w.words : null;
    }

    /* ---------- the narrator ---------- */
    sentence() { return this.seq[this.idx] || null; }
    begin(s) {
      Audio.stopGroup(GROUP);
      const name = 'vo:' + s.id, live = TS() === 1 && Audio.running();
      this.cur = { id: s.id, gap: gapAfter(s), align: null };
      if (live && Audio.has(name)) {
        const dur = Audio.duration(name) || silentDur(s), off = Math.min(this.pos, Math.max(0, dur - 0.05));
        if (off < dur - 0.06) Audio.schedule(name, { group: GROUP, bus: 'voice', offset: off });
        Object.assign(this.cur, { mode: 'clip', dur, ctx0: Audio.now() - off });
        Audio.duck(true);
        const sp = this.wordsOf(s.id);
        if (sp) this.cur.align = alignWords(PaperReader.words(s.text), sp);
      } else if (live && Audio.pending(name) && !(this.waitFor === s.id && Game.time - this.waitAt > 6)) {
        if (this.waitFor !== s.id) { this.waitFor = s.id; this.waitAt = Game.time; }
        Object.assign(this.cur, { mode: 'wait', dur: silentDur(s) });
      } else {
        Object.assign(this.cur, { mode: 'silent', dur: silentDur(s) });
        Audio.duck(false);
      }
      this.mode = this.cur.mode;
    }
    play() {
      if (this.err) return;
      if (this.done) { this.again(); return; }
      if (this.gate) { this.gateKey(); return; }
      this.playing = true; this.startedOnce = true; this.cur = null; this.follow = true;     // ▶ = "read to me": follow again
      this.reader.reveal(this.sentence().id);
    }
    pause() {
      if (!this.playing) return;
      this.playing = false; Audio.stopGroup(GROUP); Audio.duck(false);
      if (this.cur && this.cur.mode === 'clip') this.pos = Math.max(0, Audio.now() - this.cur.ctx0);
      this.cur = null;
    }
    toggle() { if (this.playing && !this.gate) this.pause(); else this.play(); }
    jumpTo(id, autoplay = true) {
      const s = this.byId[id]; if (!s) return;
      Audio.stopGroup(GROUP);
      this.idx = s.i; this.pos = 0; this.cur = null; this.gate = null; this.done = false;
      this.follow = true; this.reader.hideTerm();
      if (autoplay) { this.playing = true; this.startedOnce = true; }
      this.P.at = id; this.P.finished = false; Game.progress.save();
      this.highlight(); this.reader.reveal(id);
    }
    summary() {
      const first = this.seq.find(s => s.sec === 'summary');
      if (first) this.jumpTo(first.id, true);
    }
    back() { this.follow = true; const s = this.sentence(); this.reader.reveal(this.gate ? this.gate : s ? s.id : 'front'); }
    userScrolled() { if (this.follow) { this.follow = false; } }
    finishSentence() {
      const s = this.sentence();
      this.pos = 0; this.cur = null; Audio.stopGroup(GROUP);
      const S = this.secOf[s.sec];
      if (s.secEnd && S && S.check && !this.checkResolved(S.id)) {
        this.gate = S.id; Audio.duck(false);
        this.refreshChecks();
        if (this.follow) this.reader.keepInView(this.reader.checkEl(S.id));
        return;
      }
      this.advance();
    }
    advance() {
      if (this.reader) this.reader.keepInView(null);
      this.gate = null; this.pos = 0; this.cur = null;
      if (this.idx >= this.seq.length - 1) { this.finish(); return; }
      this.idx++;
      this.P.at = this.seq[this.idx].id; Game.progress.save();
      this.highlight();
    }
    finish() {
      this.playing = false; this.done = true; Audio.duck(false);
      this.P.done = true; this.P.finished = true; this.P.at = this.seq[0].id; Game.progress.save();
      this.refreshChecks();
      if (this.follow) this.reader.reveal('summary', { at: 0.1 });
    }
    again() { this.done = false; this.P.finished = false; this.jumpTo(this.seq[0].id, true); this.refreshChecks(); }
    toMap() { Game.replace(new Levels.map()); }

    /* ---------- checks ---------- */
    checkResolved(sec) { const c = this.P.checks[sec]; return !!c && (c.right != null || !!c.skipped); }
    answer(sec, i) {
      const S = this.secOf[sec]; if (!S || !S.check) return;
      const c = this.P.checks[sec], o = S.check.options[i]; if (!o || c.right != null || c.wrong.includes(i)) return;
      if (c.first == null) c.first = i;
      c.last = i;
      if (o.correct) { c.right = i; c.skipped = false; Audio.play('ding', { gain: 0.5 }); }
      else { c.wrong.push(i); Audio.play('thunk', { gain: 0.5 }); }
      Game.progress.save();
      this.refreshChecks();
      this.reader.keepInView(this.reader.checkEl(sec));
      if (o.correct && this.gate === sec) { this.resumeAt = this.playing ? Game.time + 2.2 : null; }
    }
    skipCheck(sec) {
      const c = this.P.checks[sec]; if (!c || c.right != null) return;
      c.skipped = true; Game.progress.save(); this.refreshChecks();
      if (this.gate === sec) { this.playing = true; this.startedOnce = true; this.advance(); if (this.follow) this.reader.reveal(this.sentence().id); }
    }
    keepReading() {
      if (!this.gate) return;
      this.resumeAt = null; this.playing = true; this.startedOnce = true; this.follow = true;
      this.advance(); this.reader.reveal(this.sentence().id);
    }
    gateKey() {               // Space / ▶ at a check: carry on once it's answered or skipped, else bring it into view
      if (this.checkResolved(this.gate)) this.keepReading();
      else { this.follow = true; this.reader.reveal(this.reader.checkEl(this.gate), { fit: true }); }
    }
    score() {
      const secs = this.M.sections.filter(S => S.check);
      const right = secs.filter(S => { const c = this.P.checks[S.id]; return c && c.first != null && S.check.options[c.first] && S.check.options[c.first].correct; }).length;
      return { right, of: secs.length };
    }
    refreshChecks() {
      if (!this.reader) return;
      this.M.sections.forEach(S => {
        if (!S.check) return;
        const c = this.P.checks[S.id];
        this.reader.setCheck(S.id, { order: c.order, wrong: c.wrong, right: c.right == null ? null : c.right, last: c.last == null ? null : c.last,
          skipped: !!c.skipped, waiting: this.gate === S.id });
      });
      const sc = this.score(), tpl = (this.M.summary && this.M.summary.score) || '';
      this.reader.setScore(tpl.replace('{right}', sc.right).replace('{of}', sc.of));
      const card = (this.M.front.step && this.M.front.step.card) || {};
      this.reader.setEnd({ show: this.done, line: card.done || '' });
    }
    /* the visible check for keys 1-3 when no check is waiting */
    keyCheck() {
      if (this.gate) return this.gate;
      const S = this.M.sections.find(q => q.check && this.reader.checkEl(q.id) && this.reader.visible(this.reader.checkEl(q.id), [0, 1]));
      return S ? S.id : null;
    }

    /* ---------- terms ---------- */
    term(t, sec) { this.reader.showTerm(t, sec); this.termAt = Game.time; }

    /* ---------- figure highlight: the parts the sentences so far in this section talk about (sticky) ---------- */
    hiFor(idx) {
      const s = this.seq[idx]; if (!s) return null;
      const S = this.secOf[s.sec]; if (!S || !S.figure || !window.PaperFigs) return null;
      // a check is waiting and unanswered: no lit row / part (it could point at an option); back once answered
      if (S.check && this.gate === S.id && !this.checkResolved(S.id)) return null;
      if (idx in this.hiCache) return this.hiCache[idx];
      let hi = S.figure.kind === 'ledger' ? { codes: new Set(), count: 0 } : null;
      for (let i = 0; i <= idx; i++) {
        const q = this.seq[i]; if (q.sec !== s.sec || q.kind === 'head') continue;
        const m = PaperFigs.match(S.figure, q.text, hi);
        if (m) hi = m;
        else if (hi && hi.codes && i === idx) hi = Object.assign({}, hi, { codes: new Set() });
      }
      return (this.hiCache[idx] = hi);
    }
    highlight() { if (this.reader) this.reader.setCurrent(this.sentence() ? this.sentence().id : null, -1); }

    /* ---------- the clock ---------- */
    update(dt) {
      if (this.err || !this.reader) return;
      this.reader.tick(dt);
      if (this.resumeAt != null && Game.time >= this.resumeAt) { this.resumeAt = null; if (this.gate && this.playing && this.checkResolved(this.gate)) this.keepReading(); }
      if (!this.playing || this.gate || this.done) { this.word = -1; return; }
      const s = this.sentence(); if (!s) return;
      if (!this.cur || this.cur.id !== s.id) this.begin(s);
      const c = this.cur;
      if (c.mode === 'wait') {
        if (!Audio.pending('vo:' + s.id) || Game.time - this.waitAt > 6) { this.waitFor = Audio.pending('vo:' + s.id) ? s.id : null; this.begin(s); }
        return;
      }
      if (c.mode === 'clip') this.pos = Audio.now() - c.ctx0;
      else this.pos += dt * TS();
      this.word = -1;
      if (c.mode === 'clip' && c.align) {
        for (let k = c.align.length - 1; k >= 0; k--) if (this.pos >= c.align[k].s - 0.02) { this.word = this.pos <= c.align[k].e + 0.25 ? k : -1; break; }
      }
      if (this.pos >= c.dur) Audio.duck(false);
      if (this.pos >= c.dur + c.gap) this.finishSentence();
    }

    /* ---------- layout: which mode, where the paper sits (CSS px), the stage boxes (logical) ---------- */
    layoutNow() {
      const docked = UI.docked(), r = Game.canvas.getBoundingClientRect(), k = r.width / 1920;
      if (docked) {
        const m = document.querySelector('#mass-dock .dk-main');
        const mr = m ? m.getBoundingClientRect() : { left: r.left, top: r.bottom, width: r.width, height: Math.max(200, innerHeight - r.bottom - 90) };
        return { mode: 'dock', k, rect: { left: mr.left, top: mr.top, width: mr.width, height: mr.height }, fs: Math.max(16, Math.min(19, mr.width / 22)),
          stage: { left: r.left + 6, top: r.top + 6, width: r.width - 12, height: r.height - 12 } };
      }
      const wide = k * 1920 >= 1150;
      const P = wide ? { x: 24, y: 24, w: 772, h: 1032, sx: 816, sw: 1080 } : { x: 16, y: 16, w: 504, h: 1048, sx: 536, sw: 1368 };
      const toCss = (x, y, w, h) => ({ left: r.left + x * k, top: r.top + y * k, width: w * k, height: h * k });
      return { mode: wide ? 'wide' : 'narrow', k, P,
        rect: toCss(P.sx + 8, 22, P.sw - 16, 1036), fs: wide ? Math.max(15, Math.min(23, 24 * k)) : Math.max(15, Math.min(18, 30 * k)),
        stage: wide ? toCss(P.x + 10, 120, P.w - 20, 760) : toCss(P.x + 4, 150, P.w - 8, 620) };
    }
    syncDom(L) {
      const R = this.reader, show = Game.current() === this && !Game._inTransition && !Game.paused;
      R.layout({ rect: L.rect, fs: L.fs, wide: L.mode === 'wide', stage: L.stage });
      R.show(show);
      if (show && this._revealOnShow) { this._revealOnShow = false; const s = this.sentence(); if (s && (this.P.at || this.startedOnce)) R.reveal(s.id, { smooth: false }); }
      R.setCurrent(this.sentence() && (this.startedOnce || this.P.at) ? this.sentence().id : null, this.word);
      R.setFollow(this.follow, this.startedOnce && !this.done);
      R.setWideNote(L.mode === 'wide' ? ' (beside the text)' : '');
      // follow mode: keep the current sentence (or the waiting check) in the comfortable band
      if (show && this.follow && this.startedOnce && !R.animating()) {
        if (this.gate) { const ce = R.checkEl(this.gate); if (ce && !R.visible(ce, [0, 1])) R.reveal(ce, { fit: true }); }
        else if (this.playing) { const s = this.sentence(); if (s && !R.visible(s.id)) R.reveal(s.id); }
      }
      if (show && L.mode !== 'wide') {
        const hi = {}, s = this.sentence();
        if (s && this.secOf[s.sec] && (this.playing || this.startedOnce)) hi[s.sec] = this.hiFor(this.idx);
        R.refreshFigures(hi);
      }
      if (this.reader.termOpen() && this.termAt != null && Game.time - this.termAt > 0.2 && Game.input.justDown) { /* a tap on the stage closes it */ R.hideTerm(); }
    }

    /* ---------- keys ---------- */
    keyDown(k) {
      if (this.err) { if (k === 'Enter') this.toMap(); return; }
      if (k === ' ' || k === 'Enter') { if (this.done && k === 'Enter') this.toMap(); else this.toggle(); return; }
      if (k === 's' || k === 'S') { this.summary(); return; }
      if (k === 'b' || k === 'B') { this.back(); return; }
      if (/^[1-9]$/.test(k)) {
        const sec = this.keyCheck(); if (!sec) return;
        const c = this.P.checks[sec], i = c.order[+k - 1];
        if (i != null) this.answer(sec, i);
        return;
      }
      const col = this.reader && this.reader.col; if (!col) return;
      const step = { ArrowDown: 0.15, ArrowUp: -0.15, PageDown: 0.85, PageUp: -0.85 }[k];
      if (step) { this.follow = false; col.scrollBy({ top: col.clientHeight * step, behavior: 'auto' }); }
    }

    /* ---------- drawing ---------- */
    draw() {
      const C = CC(), t0 = this.enteredAt;
      g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      if (this.err) {
        hand(this.err, 960, 540, 54, t0 + 0.2, { color: C.brick, seed: 9 });
        if (UI.docked()) { Dock.head('The Pamphlet', ''); this.dockBar(); }
        else if (UI.iconButton('pp-map', '⌂', UI.hudSlot(0).x, UI.hudSlot(0).y)) this.toMap();
        return;
      }
      const L = this.layoutNow(); this.L = L;
      if (L.mode === 'dock') { if (this.reader) this.drawDock(L); else this.cover(16, 16, 1888, 1048, { big: true }); }
      else this.drawSide(L);
      if (this.reader) this.syncDom(L);          // (the torn transition draws a scene after its exit(): no reader then)
    }
    speaking() {
      const s = this.sentence();
      if (!this.playing || this.gate || !s || !this.cur || this.pos > this.cur.dur) return null;
      return s.who || this.M.narrator;
    }
    env(who) {
      if (this.speaking() !== who) return 0;
      const s = this.sentence(), e = this.cur && this.cur.mode === 'clip' ? Audio.envelope('vo:' + s.id) : null;
      if (e) { const i = Math.floor(this.pos * e.hz); return e.data[Math.max(0, Math.min(e.data.length - 1, i))] || 0; }
      return 0.3 + 0.25 * Math.sin(T * 17);
    }
    sectionShown() {
      const s = this.sentence();
      if (!this.reader) return s ? s.sec : 'front';
      // once reading has begun the stage keeps the READING section's figure (it stays through that section's check, then
      // the next one comes in), whatever the student scrolls to; before that it follows the page
      if (this.gate) return this.gate;
      if (s && (this.startedOnce || this.P.at)) return s.sec;
      return this.reader.sectionInView() || (s ? s.sec : 'front');
    }
    /* the cover: the two voices and the section being read */
    cover(x, y, w, h, { big = false } = {}) {
      const C = CC(), sec = this.secOf[this.sectionShown()], sp = this.speaking();
      paperRect(x + w / 2, y + h / 2, w, h, C.parchment || '#efe2c2', { seed: 77, depth: 1.2 });
      const fsT = Math.min(w / 15, big ? 96 : 66);
      Civic.woodtype('THE PAMPHLET', x + w / 2, y + fsT * 1.1, fsT, { color: C.navy, ink: C.cream, t0: this.enteredAt + 0.1, seed: 311 });
      const label = sec ? `${sec.n}. ${sec.heading.text}` : this.sectionShown() === 'summary' ? ((this.M.summary.heading || {}).text || '') : (this.M.front.step.tag || '');
      const ls = Math.min(UI.text(big ? 58 : 40), w / 11);
      const lines = UI._wrap(label, w - 60, `${ls}px 'Caveat Brush'`).slice(0, 2);
      lines.forEach((l, i) => hand(l, x + w / 2, y + fsT * 2.5 + ls * 0.5 + i * ls * 1.05, ls, -1e6, { dur: 0.01, color: C.brick, seed: 320 + i }));
      // the voices: the Judge reads, Rex asks in the margin
      const ph = Math.min(h * 0.62, w * 0.62), fy = y + h - 6;
      Puppet.draw('rex', x + w * 0.24, fy, ph * (sp === 'rex' ? 0.98 : 0.88), { facing: 1, t: T, env: this.env('rex') });
      Puppet.draw('judge', x + w * 0.76, fy, ph, { facing: -1, t: T, env: this.env('judge') });
      if (sp) {
        const nm = (LessonHooks.speakers && LessonHooks.speakers[sp] && LessonHooks.speakers[sp].name) || sp;
        hand(`${nm} reading`, x + (sp === 'rex' ? w * 0.24 : w * 0.76), fy - ph - 10, Math.min(UI.text(34), w / 14), -1e6, { dur: 0.01, color: C.navy, seed: 330 });
      }
    }
    drawSide(L) {
      const C = CC(), P = L.P, t0 = this.enteredAt;
      // the paper sheet under the DOM page (the torn transition shows it)
      paperRect(P.sx + P.sw / 2, 540, P.sw, 1048, '#fbf5e6', { seed: 81, depth: 1.6 });
      tape(P.sx + 120, 24, 150, -0.4, undefined, 5); tape(P.sx + P.sw - 120, 24, 150, 0.4, undefined, 6);
      const secId = this.sectionShown(), S = this.secOf[secId];
      if (L.mode === 'wide' && S && S.figure) {
        const fx = P.x, fy = 112, fw = P.w, fh = 800;
        paperRect(fx + fw / 2, fy + fh / 2, fw, fh, '#fbf5e6', { seed: 82, depth: 1.4 });
        const s = this.sentence();
        const hi = s && s.sec === secId && (this.playing || this.startedOnce) ? this.hiFor(this.idx) : null;
        PaperFigs.draw(g, S.figure, { x: fx + 18, y: fy + 20, w: fw - 36, h: fh - 40, fs: Math.min(32, (fw - 36) / 24.5), hi });
        hand(`Figure ${S.n}`, fx + 20, fy - 30, 40, -1e6, { dur: 0.01, color: C.cream, align: 'left', seed: 340, outline: 'rgba(31,47,85,0.9)' });
      } else if (L.mode === 'wide') this.cover(P.x, 24, P.w, 880, { big: true });
      else this.cover(P.x, 16, P.w, 760);
      this.controls(L);
    }
    sumLabel() { return ((this.M.summary.heading && this.M.summary.heading.text) || 'Summary') + ' ⏭'; }
    controls(L) {
      const C = CC(), P = L.P, wide = L.mode === 'wide';
      const playLabel = this.done ? '↺ Read again' : this.gate && !this.checkResolved(this.gate) ? 'Answer the check' : this.playing ? '❚❚ Pause' : this.startedOnce ? '▶ Keep reading' : '▶ Read to me';
      if (wide) {
        const y = 980;
        if (UI.iconButton('pp-map', '⌂', P.x + 54, y, { r: 44 })) { this.toMap(); return; }
        if (UI.button('pp-play', playLabel, P.x + 340, y, { w: 400, h: 92, color: PAL.mustard, text: C.ink || PAL.ink, size: 46 })) this.toggle();
        if (UI.button('pp-sum', this.sumLabel(), P.x + 640, y, { w: 250, h: 92, color: C.navy, size: 34 })) this.summary();
        if (UI.keys) hand('Space: read / pause  ·  S: You Just Learned  ·  B: back to the reader  ·  1-3: answer', P.x + P.w / 2, 1052, 25, -1e6, { dur: 0.01, color: C.cream, seed: 350 });
      } else {
        const cx = P.x + P.w / 2, bw = P.w - 40;
        if (UI.iconButton('pp-map', '⌂', P.x + 60, 1080 - 70, { r: 44 })) { this.toMap(); return; }
        const bh = UI.tap(84);
        if (UI.button('pp-sum', this.sumLabel(), cx + 60, 1080 - 70, { w: bw - 140, h: 84, color: C.navy, size: 40 })) this.summary();
        if (UI.button('pp-play', playLabel, cx, 1080 - 70 - bh - 24, { w: bw, h: 84, color: PAL.mustard, text: C.ink || PAL.ink, size: 44 })) this.toggle();
      }
    }
    /* the figure of the section being read, on its own sheet (the portrait stage; 'Figure n' in a corner tab) */
    figurePanel(S, x, y, w, h) {
      const C = CC(), s = this.sentence();
      paperRect(x + w / 2, y + h / 2, w, h, '#fbf5e6', { seed: 84, depth: 1.2 });
      const hi = s && s.sec === S.id && (this.playing || this.startedOnce) ? this.hiFor(this.idx) : null;
      const H_EM = { flow: 20.2, compare: 19, quotes: 14.5, ledger: 20.4, strip: 14.2, funnel: 16.5 }[S.figure.kind] || 16;
      const fs = Math.min((w - 40) / 24.5, (h - 30) / H_EM), fw = fs * 24.5;
      PaperFigs.draw(g, S.figure, { x: x + (w - fw) / 2, y: y + 14, w: fw, h: h - 28, fs, hi });
      hand(`Fig. ${S.n}`, x + 30, y + 46, 64, -1e6, { dur: 0.01, align: 'left', color: C.brick, seed: 341 });
    }
    drawDock(L) {
      const fsec = this.secOf[this.sectionShown()];
      if (fsec && fsec.figure && (this.startedOnce || this.P.at) && window.PaperFigs) this.figurePanel(fsec, 8, 8, 1904, 1064);
      else this.cover(16, 16, 1888, 1048, { big: true });
      const s = this.sentence(), sec = s ? this.secOf[s.sec] : null;
      const checks = this.M.sections.filter(q => q.check), answered = checks.filter(q => this.checkResolved(q.id)).length;
      Dock.head('The Pamphlet', sec ? `section ${sec.n} of ${this.M.sections.length} · ${answered}/${checks.length} checks` : `${answered}/${checks.length} checks`);
      this.dockBar();
    }
    dockBar() {
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => this.toMap() });
      if (this.err) return;
      const lab = this.done ? 'again' : this.playing && !this.gate ? 'pause' : 'read';
      Dock.button({ id: 'play', glyph: this.playing && !this.gate ? '❚❚' : this.done ? '↺' : '▶', label: lab,
        aria: this.done ? 'read it again' : this.playing && !this.gate ? 'pause' : 'read to me', primary: true, onClick: () => this.toggle() });
      Dock.button({ id: 'summary', glyph: '⏭', label: 'summary', aria: 'Jump to ' + ((this.M.summary.heading && this.M.summary.heading.text) || 'the summary'), onClick: () => this.summary() });
    }

    /* ---------- QA ---------- */
    state() {
      const s = this.sentence();
      return { idx: this.idx, id: s && s.id, sec: s && s.sec, playing: this.playing, gate: this.gate, follow: this.follow, mode: this.L && this.L.mode,
        clip: this.cur && this.cur.mode, pos: this.pos, dur: this.cur && this.cur.dur, word: this.word, done: this.done, score: this.score(),
        startedOnce: this.startedOnce, n: this.seq ? this.seq.length : 0, err: this.err };
    }
  }
  Pamphlet.STEP = STEP;
  Pamphlet.alignWords = alignWords;
  Pamphlet.silentDur = silentDur;
  Pamphlet.VOICE = VOICE;
  Levels.pamphlet = Pamphlet;
})();
